import { useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useFromHash } from '../../hooks/useFromHash';

import classes from "./ProfilePage.module.css";
import NavBar from "../../components/NavBar/NavBar";
import NavBarPhone from "../../components/NavBarPhone/NavBarPhone";
import PersonalData from "../../components/ProfilePage/PersonalData";
import Notifications from "../../components/ProfilePage/Notifications";
import { logoutUser, refreshUserProfile } from '../../store/authSlice';
import { useDispatch } from 'react-redux';
import { NavBarContext } from '../../App';
import { useViewportHeight } from '../../hooks/useViewportHeight';
import useSearchParamsTabs from '../../hooks/useSearchParamsTabs';
// import { useFirstTimeModal } from '../../hooks/useFirstTimeModal';
import { useHasStudios } from '../../hooks/useHasStudios';
import useVerticalDragScroll from "../../hooks/useVerticalDragScroll";
import Auszahlung from "../../components/ProfilePage/Auszahlung";
import HelpMenu from "../../components/NavBar/HelpMenu";
import { useHelpMenuTrigger } from '../../hooks/useHelpMenuTrigger';
// import { ListStudioPhone } from "../../components/ListStudioModal/ListStudioModal";

/** Query values for ?konto=… on /profile. Auszahlung is provider-only and hidden for users without studios. */
export const PROFILE_KONTO_TAB_IDS = Object.freeze(['personal', 'notifications', 'auszahlung']);

export const ProfilePage = () => {
    const authUser = useSelector((state) => state?.auth?.user);
    const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
    const { profilePicture, firstName, lastName } = authUser || {};
    const userId = authUser?.id || authUser?._id;
    const { activeView, setActiveView } = useContext(NavBarContext);
    const location = useLocation();
    const { activeId: activeSection, setTabIndex } = useSearchParamsTabs({
        paramName: 'konto',
        tabIds: PROFILE_KONTO_TAB_IDS,
        defaultIndex: 0,
    });
    const navigate = useNavigate();
    const { getInternalLinkProps } = useFromHash();
    const dispatch = useDispatch();
    
    // Initialize viewport height fix for iOS Safari
    useViewportHeight();

    // Track first-time modal display
    // const { shouldShowModal, markModalAsSeen } = useFirstTimeModal(userId);

    const { hasStudios, loading: hasStudiosLoading } = useHasStudios(userId);

    const helpMenu = useHelpMenuTrigger({ hoverCloseDelay: 300 });

    const phoneContentRef = useRef(null);
    const mobileHeaderRef = useRef(null);
    const mobileFooterRef = useRef(null);

    useVerticalDragScroll({
        sourceRefs: [mobileHeaderRef, mobileFooterRef],
        scrollTargetRef: phoneContentRef,
    });

    useEffect(() => {
        const iosVersionMatch = navigator.userAgent.match(/OS (\d+)[._](\d+)[._](\d+)/);
        const isIos2642 = Boolean(
            iosVersionMatch &&
            iosVersionMatch[1] === "26" &&
            iosVersionMatch[2] === "4" &&
            iosVersionMatch[3] === "2"
        );

        if (isIos2642) {
            document.documentElement.classList.add("ios-26-4-2");
        }

        return () => {
            if (isIos2642) {
                document.documentElement.classList.remove("ios-26-4-2");
            }
        };
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.has('konto')) return;
        const fromState = location.state?.activeSection;
        if (fromState && PROFILE_KONTO_TAB_IDS.includes(fromState)) {
            setTabIndex(PROFILE_KONTO_TAB_IDS.indexOf(fromState));
        }
    }, [location.state, location.search, setTabIndex]);

    useEffect(() => {
        if (isAuthenticated && userId) {
            dispatch(refreshUserProfile());
        }
    }, [dispatch, isAuthenticated, userId]);

    useEffect(() => {
        if (hasStudiosLoading || hasStudios || activeSection !== 'auszahlung') return;
        setTabIndex(0);
    }, [activeSection, hasStudios, hasStudiosLoading, setTabIndex]);

    const handleNavigateToAccountDetails = () => {
        navigate('/profile/account-details');
    };
    
    const handleLogout = () => {
        dispatch(logoutUser());
        navigate('/');
    };    

    const activeStyle = {
        borderRadius: '8px',
        background: 'var(--light-grey-2)'
    };

    const renderContent = () => {
        switch(activeSection) {
            case 'personal':
                return <PersonalData/>;
            case 'notifications':
                return <Notifications/>;
            case "auszahlung":
                return hasStudios ? <Auszahlung/> : <PersonalData/>;
            default:
                return <PersonalData/>;
        }
    };

  return (
    <>
    <div className={classes.container}>
        <NavBar fromProfile={true} />
        <div className={classes.mainContainer}>
            <div className={classes.navigationContainer}>
                <h1 className="titleSemibold2">Konto</h1>
                <ul className={`${classes.navigationList} titleSemibold3`}>
                    <li 
                        onClick={() => setTabIndex(0)}
                        style={activeSection === 'personal' ? activeStyle : {}}
                    >
                        Persönliche Daten
                    </li>
                    {/* <li>Log-In & Sicherheit</li> */}
                    {/* <li>System Einstellungen</li> */}
                    <li 
                        onClick={() => setTabIndex(1)}
                        style={activeSection === 'notifications' ? activeStyle : {}}
                    >
                        Benachrichtigungen
                    </li>
                    {hasStudios && (
                        <li
                            onClick={() => setTabIndex(2)}
                            style={activeSection === 'auszahlung' ? activeStyle : {}}
                        >
                            Auszahlung
                        </li>
                    )}
                </ul>
            </div>
            <div className={classes.verticalLine}></div>
            <div className={classes.contentContainer}>
                {renderContent()}
            </div>
        </div>
    </div>
    <div className={classes.mobileContainer}>
        <header ref={mobileHeaderRef} className={classes.mobileHeader}>
            <Link to="/">
                <svg xmlns="http://www.w3.org/2000/svg" width="53" height="18" viewBox="0 0 53 18" fill="none">
                    <path d="M50.5002 9C51.8809 9 53.0002 7.88071 53.0002 6.5C53.0002 5.11929 51.8809 4 50.5002 4C49.1195 4 48.0002 5.11929 48.0002 6.5C48.0002 7.88071 49.1195 9 50.5002 9Z" fill="#FF003E"/>
                    <path d="M9.93692 9.28445L8.58239 9.17069C5.23081 8.89942 4.10204 8.5494 4.10204 7.7881C4.10204 7.0268 5.52603 6.60677 8.46083 6.60677C11.5606 6.60677 12.8109 7.01805 13.0714 8.12062L13.1148 8.29563H16.5185L16.4838 8.05062C16.1452 5.14542 13.5577 3.73658 8.58239 3.73658C3.60712 3.73658 1.05437 5.11917 1.05437 7.83185C1.05437 10.317 3.09483 11.5158 8.11352 11.9709L9.32912 12.0846C12.6546 12.3734 13.8876 12.8109 13.8876 13.686C13.8876 14.6398 12.3681 15.1298 9.36385 15.1298C5.96017 15.1298 4.2236 14.491 4.05863 13.1697L4.03258 12.9772H0.628906V13.2135C0.785197 16.3899 3.63317 18 9.10336 18C14.5736 18 16.944 16.4949 16.944 13.5285C16.944 10.8858 14.9817 9.69572 9.93692 9.28445Z" fill="#1F1F1F"/>
                    <path d="M42.1415 4.17403V9.8444C42.1415 13.0209 40.0663 14.9897 36.7321 14.9897C33.5976 14.9897 32.3733 13.5809 32.3733 9.95816V4.17403H21.8497V0L18.7152 1.33009V13.2834C18.7152 16.5823 20.2954 17.9912 24.003 17.9912C25.2968 17.9912 26.5384 17.7724 27.589 17.5537L27.7627 17.5187V14.7097L27.5022 14.7622C26.139 15.0422 25.5312 15.1473 24.5587 15.1473C22.7614 15.1473 21.8497 14.7185 21.8497 12.3646V6.7992H30.793L29.1433 8.05928V10.982C29.1433 15.7685 31.1577 17.9912 35.4731 17.9912C38.2603 17.9912 40.5525 17.0199 42.1415 15.1648V17.5537H45.3715V4.17403H42.1415Z" fill="#1F1F1F"/>
                </svg>
            </Link>
        </header>
        <div ref={phoneContentRef} className={classes.phoneContentContainer}>
            <Link to={`/users/show/${userId}`} className={classes.userPhoneContainer}>   
                <div className={classes.userInfoContainer}>
                    <div className={classes.userImageContainer}>
                        <img src={profilePicture} alt="profile" />
                    </div>
                    <div className={classes.userInfo}>
                        <h1 className="mediumLarge">{firstName} {lastName}</h1>
                        <span className="regularMiddle">Profil</span>
                    </div>
                </div>
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="15" viewBox="0 0 8 15" fill="none">
                        <path d="M7.99844 7.6998L7.59844 8.0998L1.49844 14.1998L0.398438 13.0998L5.79844 7.6998L0.398438 1.8998L1.59844 0.799805L7.99844 7.6998Z" fill="#696F76"/>
                    </svg>
                </div>
            </Link>
            <h1 className={`semiboldLarge ${classes.settingsTitle}`}>Einstellungen</h1>
            <div className={`${classes.settingsContainer} ${classes.settingsContainerHelp}`}>
                <div className={classes.settingItem} onClick={handleNavigateToAccountDetails}>
                    <div className={classes.settingItemIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
                            <mask id="path-1-inside-1_7880_4879" fill="white">
                            <path d="M17.9348 16.6009H0V16.1948C0 12.1996 3.24848 8.95117 7.24367 8.95117H10.6911C14.6863 8.95117 17.9348 12.1996 17.9348 16.1948V16.6009ZM0.825753 15.7888H17.109C16.8992 12.4313 14.1004 9.76329 10.6911 9.76329H7.24367C3.83441 9.76329 1.0356 12.4313 0.825753 15.7888Z"/>
                            <path d="M8.96861 9.76306C6.41506 9.76306 4.33844 7.68644 4.33844 5.13289C4.33844 2.57935 6.41506 0.5 8.96861 0.5C11.5222 0.5 13.5988 2.57663 13.5988 5.13017C13.5988 7.68371 11.5222 9.76034 8.96861 9.76034V9.76306ZM8.96861 1.31484C6.862 1.31484 5.15055 3.02629 5.15055 5.13289C5.15055 7.2395 6.86473 8.95094 8.96861 8.95094C11.0725 8.95094 12.7867 7.2395 12.7867 5.13289C12.7867 3.02629 11.0752 1.31484 8.96861 1.31484Z"/>
                            </mask>
                            <path d="M17.9348 16.6009H0V16.1948C0 12.1996 3.24848 8.95117 7.24367 8.95117H10.6911C14.6863 8.95117 17.9348 12.1996 17.9348 16.1948V16.6009ZM0.825753 15.7888H17.109C16.8992 12.4313 14.1004 9.76329 10.6911 9.76329H7.24367C3.83441 9.76329 1.0356 12.4313 0.825753 15.7888Z" fill="#1F1F1F"/>
                            <path d="M8.96861 9.76306C6.41506 9.76306 4.33844 7.68644 4.33844 5.13289C4.33844 2.57935 6.41506 0.5 8.96861 0.5C11.5222 0.5 13.5988 2.57663 13.5988 5.13017C13.5988 7.68371 11.5222 9.76034 8.96861 9.76034V9.76306ZM8.96861 1.31484C6.862 1.31484 5.15055 3.02629 5.15055 5.13289C5.15055 7.2395 6.86473 8.95094 8.96861 8.95094C11.0725 8.95094 12.7867 7.2395 12.7867 5.13289C12.7867 3.02629 11.0752 1.31484 8.96861 1.31484Z" fill="#1F1F1F"/>
                            <path d="M17.9348 16.6009V18.6009H19.9348V16.6009H17.9348ZM0 16.6009H-2V18.6009H0V16.6009ZM0.825753 15.7888L-1.17035 15.664L-1.30315 17.7888H0.825753V15.7888ZM17.109 15.7888V17.7888H19.2379L19.1051 15.664L17.109 15.7888ZM8.96861 9.76306V11.7631H10.9686V9.76306H8.96861ZM8.96861 9.76034V7.76034H6.96861V9.76034H8.96861ZM17.9348 14.6009H0V18.6009H17.9348V14.6009ZM2 16.6009V16.1948H-2V16.6009H2ZM2 16.1948C2 13.3042 4.35305 10.9512 7.24367 10.9512V6.95117C2.14391 6.95117 -2 11.0951 -2 16.1948H2ZM7.24367 10.9512H10.6911V6.95117H7.24367V10.9512ZM10.6911 10.9512C13.5817 10.9512 15.9348 13.3042 15.9348 16.1948H19.9348C19.9348 11.0951 15.7909 6.95117 10.6911 6.95117V10.9512ZM15.9348 16.1948V16.6009H19.9348V16.1948H15.9348ZM0.825753 17.7888H17.109V13.7888H0.825753V17.7888ZM19.1051 15.664C18.8299 11.2598 15.1638 7.76329 10.6911 7.76329V11.7633C13.0369 11.7633 14.9685 13.6028 15.1129 15.9135L19.1051 15.664ZM10.6911 7.76329H7.24367V11.7633H10.6911V7.76329ZM7.24367 7.76329C2.77096 7.76329 -0.895087 11.2598 -1.17035 15.664L2.82186 15.9135C2.96628 13.6028 4.89785 11.7633 7.24367 11.7633V7.76329ZM8.96861 7.76306C7.51963 7.76306 6.33844 6.58187 6.33844 5.13289H2.33844C2.33844 8.79101 5.31049 11.7631 8.96861 11.7631V7.76306ZM6.33844 5.13289C6.33844 3.68239 7.52117 2.5 8.96861 2.5V-1.5C5.30896 -1.5 2.33844 1.47632 2.33844 5.13289H6.33844ZM8.96861 2.5C10.4176 2.5 11.5988 3.68119 11.5988 5.13017H15.5988C15.5988 1.47206 12.6267 -1.5 8.96861 -1.5V2.5ZM11.5988 5.13017C11.5988 6.57914 10.4176 7.76034 8.96861 7.76034V11.7603C12.6267 11.7603 15.5988 8.78828 15.5988 5.13017H11.5988ZM6.96861 9.76034V9.76306H10.9686V9.76034H6.96861ZM8.96861 -0.685156C5.75743 -0.685156 3.15055 1.92172 3.15055 5.13289H7.15055C7.15055 4.13086 7.96657 3.31484 8.96861 3.31484V-0.685156ZM3.15055 5.13289C3.15055 8.345 5.76109 10.9509 8.96861 10.9509V6.95094C7.96837 6.95094 7.15055 6.134 7.15055 5.13289H3.15055ZM8.96861 10.9509C12.1761 10.9509 14.7867 8.345 14.7867 5.13289H10.7867C10.7867 6.134 9.96885 6.95094 8.96861 6.95094V10.9509ZM14.7867 5.13289C14.7867 1.92172 12.1798 -0.685156 8.96861 -0.685156V3.31484C9.97064 3.31484 10.7867 4.13086 10.7867 5.13289H14.7867Z" fill="#1F1F1F" mask="url(#path-1-inside-1_7880_4879)"/>
                        </svg>
                        <span className="regularMiddle">Konto</span>
                    </div>
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                            <path d="M7.99844 7.4L7.59844 7.8L1.49844 13.9L0.398438 12.8L5.79844 7.4L0.398438 1.6L1.59844 0.5L7.99844 7.4Z" fill="#696F76"/>
                        </svg>
                    </div>     
                </div>
                <div className={classes.horizontalLine}></div>
                <div
                    ref={helpMenu.containerRef}
                    className={classes.helpMenuItem}
                >
                    <div
                        className={`${classes.settingItem} ${helpMenu.isActive ? classes.settingItemActive : ''}`}
                        onClick={helpMenu.onClick}
                    >
                        <div className={classes.settingItemIcon}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                                <path d="M8.08878 11.5463C8.08878 11.1449 8.12145 10.8089 8.18678 10.5383C8.25211 10.2676 8.36878 10.0156 8.53678 9.78226C8.71412 9.54892 8.95678 9.31559 9.26478 9.08226C9.61945 8.79292 9.90411 8.54093 10.1188 8.32626C10.3428 8.11159 10.5061 7.89226 10.6088 7.66826C10.7114 7.43492 10.7628 7.14559 10.7628 6.80026C10.7628 6.25892 10.5901 5.84826 10.2448 5.56826C9.90878 5.28826 9.44678 5.14826 8.85878 5.14826C8.44811 5.14826 8.07011 5.19959 7.72478 5.30226C7.38878 5.40492 7.05745 5.53559 6.73078 5.69426L6.40878 4.96626C6.80078 4.77959 7.19278 4.63492 7.58478 4.53226C7.98611 4.42026 8.42945 4.36426 8.91478 4.36426C9.75478 4.36426 10.4174 4.57426 10.9028 4.99426C11.3881 5.41426 11.6308 6.01159 11.6308 6.78626C11.6308 7.22493 11.5561 7.59826 11.4068 7.90626C11.2668 8.21426 11.0661 8.49426 10.8048 8.74626C10.5528 8.99826 10.2494 9.25959 9.89478 9.53026C9.61478 9.75426 9.39545 9.96426 9.23678 10.1603C9.07811 10.3563 8.96611 10.5663 8.90078 10.7903C8.84478 11.0143 8.81678 11.2943 8.81678 11.6303V11.8403H8.08878V11.5463ZM7.82278 13.9123C7.82278 13.6603 7.87878 13.4736 7.99078 13.3523C8.11211 13.2309 8.28012 13.1703 8.49478 13.1703C8.70012 13.1703 8.85878 13.2309 8.97078 13.3523C9.09211 13.4736 9.15278 13.6603 9.15278 13.9123C9.15278 14.1643 9.09211 14.3556 8.97078 14.4863C8.85878 14.6076 8.70012 14.6683 8.49478 14.6683C8.28012 14.6683 8.11211 14.6076 7.99078 14.4863C7.87878 14.3556 7.82278 14.1643 7.82278 13.9123Z" fill="#1F1F1F"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M17.6 9.5002C17.6 14.3603 13.6601 18.3002 8.8 18.3002C3.93989 18.3002 0 14.3603 0 9.5002C0 4.64009 3.93989 0.700195 8.8 0.700195C13.6601 0.700195 17.6 4.64009 17.6 9.5002ZM8.8 17.5002C13.2183 17.5002 16.8 13.9185 16.8 9.5002C16.8 5.08192 13.2183 1.5002 8.8 1.5002C4.38172 1.5002 0.8 5.08192 0.8 9.5002C0.8 13.9185 4.38172 17.5002 8.8 17.5002Z" fill="#1F1F1F"/>
                            </svg>
                            <span className="regularMiddle">Hilfe</span>
                        </div>
                        <div>
                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                                <path d="M7.99844 7.4L7.59844 7.8L1.49844 13.9L0.398438 12.8L5.79844 7.4L0.398438 1.6L1.59844 0.5L7.99844 7.4Z" fill="#696F76"/>
                            </svg>
                        </div>
                    </div>
                </div>
                <div className={classes.horizontalLine}></div>
                <Link to="/finanzen" className={classes.settingItem}>
                    <div className={classes.settingItemIcon}>
                        <svg fill="#000000" width="21px" height="20px" viewBox="0 0 256 256" id="Flat" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M224,52H32A12.01343,12.01343,0,0,0,20,64V96.79443c-.00024.02-.00586.03809-.00586.05811s.00562.03809.00586.0581V192a12.01343,12.01343,0,0,0,12,12H224a12.01343,12.01343,0,0,0,12-12V64A12.01343,12.01343,0,0,0,224,52ZM32,60H224a4.00427,4.00427,0,0,1,4,4V92.85254H28V64A4.00427,4.00427,0,0,1,32,60ZM224,196H32a4.00427,4.00427,0,0,1-4-4V100.85254H228V192A4.00427,4.00427,0,0,1,224,196Zm-20.00586-28a4.0002,4.0002,0,0,1-4,4h-32a4,4,0,0,1,0-8h32A4.0002,4.0002,0,0,1,203.99414,168Zm-64,0a4.0002,4.0002,0,0,1-4,4h-16a4,4,0,0,1,0-8h16A4.0002,4.0002,0,0,1,139.99414,168Z"></path> </g></svg>
                        <span className="regularMiddle">Finanzen</span>
                    </div>
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                            <path d="M7.99844 7.4L7.59844 7.8L1.49844 13.9L0.398438 12.8L5.79844 7.4L0.398438 1.6L1.59844 0.5L7.99844 7.4Z" fill="#696F76"/>
                        </svg>
                    </div>    
                </Link>
            </div>
            <h1 className={`semiboldLarge ${classes.settingsTitle}`}>Anbieten</h1>
            {hasStudios ? (
            <div className={classes.settingsContainer}>
                <button 
                    onClick={() => setActiveView(activeView === 'explorer' ? 'creator' : 'explorer')} 
                    className={classes.settingItem}
                >
                    <div className={classes.settingItemIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.86474 1.50488C13.9513 1.50488 19.0165 5.76696 19.0165 11.1616V11.1721L18.9448 14.2636L22.3256 10.8137L22.9684 11.4437L18.5198 15.9832L17.764 15.2274L14.0645 11.4433L14.708 10.8141L18.0454 14.2277L18.1165 11.1566C18.1133 6.38383 13.5844 2.40488 7.86474 2.40488V1.50488ZM4.4836 6.06859L8.93843 10.6145L8.29563 11.2444L4.93778 7.81793V11.581C4.93778 16.1831 9.44481 20.0421 15.1896 20.0421V20.9421C9.10916 20.9421 4.03778 16.8275 4.03778 11.581V7.82473L0.686507 11.2525L0.0429688 10.6233L3.74127 6.84061L4.4836 6.06859Z" fill="#1F1F1F"/>
                        </svg>
                        <span className="regularMiddle">
                            {activeView === 'explorer' ? 'In die Anbieter Ansicht wechseln' : 'In die Endecker Ansicht wechseln'}
                        </span>
                    </div>
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                            <path d="M7.99844 7.4L7.59844 7.8L1.49844 13.9L0.398438 12.8L5.79844 7.4L0.398438 1.6L1.59844 0.5L7.99844 7.4Z" fill="#696F76"/>
                        </svg>
                    </div>     
                </button>
                <div className={classes.horizontalLine}></div>
                <Link {...getInternalLinkProps('/studio-erstellen')} className={classes.settingItem}>
                    <div className={classes.settingItemIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M14.9155 9.86641H3.02305V9.06641H14.9155V9.86641Z" fill="#1F1F1F"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M9.36641 3.525V15.4174H8.56641V3.525H9.36641Z" fill="#1F1F1F"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M3.18897 1.3C1.86958 1.3 0.8 2.36958 0.8 3.68897V15.2421C0.8 16.5615 1.86958 17.631 3.18897 17.631H14.7421C16.0615 17.631 17.131 16.5615 17.131 15.2421V3.68897C17.131 2.36958 16.0615 1.3 14.7421 1.3H3.18897ZM0 3.68897C0 1.92775 1.42775 0.5 3.18897 0.5H14.7421C16.5033 0.5 17.931 1.92775 17.931 3.68897V15.2421C17.931 17.0033 16.5033 18.431 14.7421 18.431H3.18897C1.42775 18.431 0 17.0033 0 15.2421V3.68897Z" fill="#1F1F1F"/>
                        </svg>
                        <span className="regularMiddle">Studio anbieten</span>
                    </div>
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                            <path d="M7.99844 7.4L7.59844 7.8L1.49844 13.9L0.398438 12.8L5.79844 7.4L0.398438 1.6L1.59844 0.5L7.99844 7.4Z" fill="#696F76"/>
                        </svg>
                    </div>     
                </Link>
            </div>
            ) : (
                <Link {...getInternalLinkProps('/studio-erstellen')} className={classes.studioAnbietenLink}>
                    <div className={classes.settingItemIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M14.9155 9.86641H3.02305V9.06641H14.9155V9.86641Z" fill="#1F1F1F"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M9.36641 3.525V15.4174H8.56641V3.525H9.36641Z" fill="#1F1F1F"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M3.18897 1.3C1.86958 1.3 0.8 2.36958 0.8 3.68897V15.2421C0.8 16.5615 1.86958 17.631 3.18897 17.631H14.7421C16.0615 17.631 17.131 16.5615 17.131 15.2421V3.68897C17.131 2.36958 16.0615 1.3 14.7421 1.3H3.18897ZM0 3.68897C0 1.92775 1.42775 0.5 3.18897 0.5H14.7421C16.5033 0.5 17.931 1.92775 17.931 3.68897V15.2421C17.931 17.0033 16.5033 18.431 14.7421 18.431H3.18897C1.42775 18.431 0 17.0033 0 15.2421V3.68897Z" fill="#1F1F1F"/>
                        </svg>
                        <span className="regularMiddle">Studio anbieten</span>
                    </div>
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                            <path d="M7.99844 7.4L7.59844 7.8L1.49844 13.9L0.398438 12.8L5.79844 7.4L0.398438 1.6L1.59844 0.5L7.99844 7.4Z" fill="#696F76"/>
                        </svg>
                    </div>  
                </Link>
            )}
            <div style={{marginTop: '10px'}} className={classes.settingsContainer}>
                <div className={classes.settingItem} onClick={handleLogout}>
                    <div className={classes.settingItemIcon} >
                        <svg xmlns="http://www.w3.org/2000/svg" width="23" height="21" viewBox="0 0 23 21" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M5.47026 1.87615C5.47026 1.44443 5.04296 1.14272 4.63612 1.28718L1.16586 2.51943C0.916562 2.60796 0.75 2.84385 0.75 3.1084V17.4681C0.75 17.7186 0.899556 17.9449 1.13 18.0431L4.60026 19.5218C5.01253 19.6974 5.47026 19.3949 5.47026 18.9468V1.87615ZM4.38516 0.580417C5.2802 0.262598 6.22026 0.926356 6.22026 1.87615V18.9468C6.22026 19.9327 5.21325 20.5982 4.30626 20.2117L0.835997 18.733C0.329023 18.517 0 18.0192 0 17.4681V3.1084C0 2.52639 0.366436 2.00742 0.914899 1.81267L4.38516 0.580417ZM12.6471 2.56731H7.30664V1.81731H12.6471C13.4065 1.81731 14.0221 2.43292 14.0221 3.19231V8.45989H13.2721V3.19231C13.2721 2.84714 12.9923 2.56731 12.6471 2.56731ZM21.3298 10.081L17.6362 6.38716L18.1665 5.85684L22.4943 10.1849L22.7808 10.447L18.1665 15.0616L17.6362 14.5313L21.3362 10.831H10.375V10.081H21.3298ZM3.8 11.7658V9.1552H4.55V11.7658H3.8ZM13.2721 17.7287V12.4599H14.0221V17.7287C14.0221 18.488 13.4065 19.1037 12.6471 19.1037H7.30664V18.3537H12.6471C12.9923 18.3537 13.2721 18.0738 13.2721 17.7287Z" fill="#1F1F1F"/>
                        </svg>
                        <span className="regularMiddle">Abmelden</span>
                    </div>
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                            <path d="M7.99844 7.4L7.59844 7.8L1.49844 13.9L0.398438 12.8L5.79844 7.4L0.398438 1.6L1.59844 0.5L7.99844 7.4Z" fill="#696F76"/>
                        </svg>
                    </div>     
                </div>
            </div>

            {hasStudios ? (
                <div className={classes.switchButtonContainer}>
                    <button 
                        className={`${classes.switchButton} slimRegularLargeText`}
                        onClick={() => setActiveView('explorer')}
                        style={activeView === 'explorer' ? { background: 'var(--black)'} : {}}
                    >
                        Entdecker
                    </button>
                    <button 
                        className={`${classes.switchButton} slimRegularLargeText`}
                        onClick={() => setActiveView('creator')}
                        style={activeView === 'creator' ? { background: 'var(--black)'} : {}}
                    >
                        Creator Ansicht
                    </button>
                </div>
            ) : (
                <Link {...getInternalLinkProps('/studio-erstellen')} className={classes.studioAnbietenButton}>
                    <span className="slimRegularLargeText">Studio anbieten</span>
                </Link>
            )
            }

        </div>
        {/* {isPhoneSize && shouldShowModal && 
            <ListStudioPhone initialOpen={true} onClose={markModalAsSeen} />
        } */}
        <div ref={mobileFooterRef}>
            <NavBarPhone fromProfile={true} activeView={activeView}/>
        </div>
        <HelpMenu
            variant="sheet"
            panelRef={helpMenu.panelRef}
            open={helpMenu.isOpen}
            onClose={helpMenu.close}
        />
    </div>
    </>
  );
};

export default ProfilePage;
